import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { DebugInstrumentor } from '@/components/general/DebugInstrumentor';
import { ErrorBoundary } from '@/components/general/ErrorBoundary';
import { Home } from '@/components/general/Home';
import { AuthenticatedEntryRedirect } from '@/components/routing/AuthenticatedEntryRedirect';
import { ProtectedLayout } from '@/components/routing/ProtectedLayout';
import { DashboardSkeleton } from '@/features/dashboard/components/DashboardSkeleton';
import { FAQSkeleton } from '@/features/faq/FAQSkeleton';
// WHY: GoalsSkeleton is lightweight markup only — negligible bundle cost even when Goals is flagged off.
// The meaningful bundle savings come from the conditional lazy() for GoalsPage and GoalDiscoveryPage.
import { GoalsSkeleton } from '@/features/goals/GoalsSkeleton';
import { LearningSkeleton } from '@/features/learning/components/LearningSkeleton';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { OnboardingSkeleton } from '@/features/onboarding/components/OnboardingSkeleton';
import { ProfileSkeleton } from '@/features/profile/components/ProfileSkeleton';
import { SessionsSkeleton } from '@/features/sessions/components/SessionsSkeleton';
import { TasksSkeleton } from '@/features/tasks/components/TasksSkeleton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AppShellSkeleton } from '@/layouts/MainLayout/AppShellSkeleton';
import { ContentRouteFallback } from './content-route-fallback';
import { featureFlags } from './featureFlags';

// --- Lazy Loads ---

const MainLayout = lazy(() => import('@/layouts/MainLayout').then((module) => ({ default: module.MainLayout })));

const OnboardingPage = lazy(() =>
  import('@/features/onboarding').then((module) => ({ default: module.OnboardingPage }))
);

const PrevMeetingRoom = lazy(() =>
  import('@/features/sessions/components/PrevMeetingRoom').then((module) => ({ default: module.PrevMeetingRoom }))
);

const FinalAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/FinalAssessmentSurvey').then((module) => ({
    default: module.FinalAssessmentSurvey
  }))
);
const MidCohortAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/MidCohortAssessmentSurvey').then((module) => ({
    default: module.MidCohortAssessmentSurvey
  }))
);
const PilotGroupAssessment = lazy(() =>
  import('@/features/tasks/components/PilotGroupAssessment').then((module) => ({
    default: module.PilotGroupAssessment
  }))
);
const OneOnOneFinalAssessment = lazy(() =>
  import('@/features/tasks/components/OneOnOneFinalAssessment').then((module) => ({
    default: module.OneOnOneFinalAssessment
  }))
);
const CoachFitAssessmentSurvey = lazy(() =>
  import('@/features/tasks/components/CoachFitAssessmentSurvey').then((module) => ({
    default: module.CoachFitAssessmentSurvey
  }))
);
const FirstSessionEvaluation = lazy(() =>
  import('@/features/tasks/components/FirstSessionEvaluation').then((module) => ({
    default: module.FirstSessionEvaluation
  }))
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const ProfilePage = lazy(() =>
  import('@/features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const SessionsPage = lazy(() =>
  import('@/features/sessions/SessionsPage').then((module) => ({ default: module.SessionsPage }))
);
const LearningPage = lazy(() =>
  import('@/features/learning/LearningPage').then((module) => ({ default: module.LearningPage }))
);
const TasksPage = lazy(() => import('@/features/tasks/TasksPage').then((module) => ({ default: module.TasksPage })));

const GoalsPage = featureFlags.goals
  ? lazy(() => import('@/features/goals').then((module) => ({ default: module.GoalsPage })))
  : null;

const GoalDiscoveryPage = featureFlags.goals
  ? lazy(() => import('@/features/goals').then((module) => ({ default: module.GoalDiscoveryPage })))
  : null;

const FAQPage = lazy(() => import('@/features/faq/FAQPage').then((module) => ({ default: module.FAQPage })));

// CONSTRAINT: lazy() stays INSIDE the ternary (dead-code elimination invariant) — a disabled flag
// must ship zero wizard-harness bytes.
const WizardHarness = featureFlags.wizardHarness
  ? lazy(() => import('@/mobile/wizard/WizardHarness').then((module) => ({ default: module.WizardHarness })))
  : null;

// WHY: Derive a stable, low-cardinality Sentry grouping key from the route's leading path segment
// (the feature area) so every lazy route gets a meaningful boundary name from the single seam below
// — without hand-wrapping each route. The raw pathname would explode grouping cardinality via
// :userId / :taskId params; `route:first-session-evaluation` groups that page's crashes together.
function routeBoundaryName(pathname: string): string {
  const segment = pathname.split('/').find(Boolean);
  return segment ? `route:${segment}` : 'route:root';
}

// WHY: One seam wraps every lazy route in a page-level ErrorBoundary, so a single page's render
// throw (or lazy chunk-load failure) renders the shared ErrorState in place while the MainLayout
// shell — mounted above this boundary via the Outlet — survives. The boundary sits OUTSIDE Suspense
// so it also catches chunk-load rejections. `name` is location-derived for Sentry grouping.
// CONSTRAINT: reuse the ErrorBoundary default fallback (ErrorState + in-place "Try again"); do not
// pass a static custom fallback here or the boundary self-reset (retry) is lost.
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary name={routeBoundaryName(pathname)}>{children}</ErrorBoundary>;
}

const renderLazyRoute = (element: ReactNode, fallback: ReactNode = <AppShellSkeleton />) => (
  <RouteErrorBoundary>
    <Suspense fallback={fallback}>{element}</Suspense>
  </RouteErrorBoundary>
);

interface AppRouterProps {
  isAuthenticated: boolean;
}

// WHY: on phones the toast sits bottom-center, floating just above the footer stack — thumb-adjacent
// and clear of the bottom tab bar / join banner (MOB-03; Dave 2026-07-18: bottom, above footer). The
// bottom offset mirrors the panes' exact footer-stack inset — --strip-footer-height (contextual/join
// footer) + --bottom-nav-height (tab bar, which already includes safe-bottom) — both published
// globally on documentElement (StripFooter/BottomNav), so they resolve against sonner's body-portaled
// toaster; a small gap floats it off the footer. Both `offset` (sonner's desktop-width band) and
// `mobileOffset` (its own <=600px band) carry it across the whole <=767px range. Desktop keeps the
// default bottom-right.
function AppToaster() {
  const isMobile = useIsMobile();
  const mobileBottomOffset = {
    bottom: 'calc(var(--strip-footer-height, 0px) + var(--bottom-nav-height, 0px) + var(--spacing-2))'
  };
  return (
    <Toaster
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      offset={isMobile ? mobileBottomOffset : undefined}
      mobileOffset={isMobile ? mobileBottomOffset : undefined}
    />
  );
}

/**
 * Orchestrates the application's routing table and global UI elements like Toast.
 */
export function AppRouter({ isAuthenticated }: AppRouterProps) {
  return (
    <BrowserRouter future={{ v7_startTransition: true }}>
      {import.meta.env.DEV && <DebugInstrumentor />}
      <ErrorBoundary name="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={isAuthenticated ? <AuthenticatedEntryRedirect /> : <Home />} />
          <Route path="/meetings/:meetingId" element={renderLazyRoute(<PrevMeetingRoom />, <AppShellSkeleton />)} />
          <Route path="/:code/all" element={renderLazyRoute(<OnboardingPage />, <OnboardingSkeleton />)} />

          {/* Assessment routes — standalone (no MainLayout), use content-only fallback */}
          <Route
            path="/first-session-evaluation/:userId/:taskId/:groupId"
            element={renderLazyRoute(<FirstSessionEvaluation />, <ContentRouteFallback />)}
          />
          <Route
            path="/final-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<FinalAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/pilot-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<PilotGroupAssessment />, <ContentRouteFallback />)}
          />
          <Route
            path="/mid-cohort-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<MidCohortAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/coach-fit-assessment/:userId/:taskId/:groupId"
            element={renderLazyRoute(<CoachFitAssessmentSurvey />, <ContentRouteFallback />)}
          />
          <Route
            path="/one-on-one-final/:userId/:taskId/:groupId"
            element={renderLazyRoute(<OneOnOneFinalAssessment />, <ContentRouteFallback />)}
          />

          {/* Protected routes */}
          <Route element={<ProtectedLayout />}>
            {featureFlags.goals && GoalDiscoveryPage && (
              <Route
                path="/goals/discovery"
                element={renderLazyRoute(<GoalDiscoveryPage />, <ContentRouteFallback />)}
              />
            )}
            {/* Wizard filmstrip dev harness (MOB-12) — standalone full-screen route, no MainLayout. */}
            {featureFlags.wizardHarness && WizardHarness && (
              <Route path="/dev/wizard" element={renderLazyRoute(<WizardHarness />, <ContentRouteFallback />)} />
            )}
            {/* MainLayout — full AppShellSkeleton while layout chunk loads */}
            <Route element={renderLazyRoute(<MainLayout />)}>
              {/* Page routes — per-page skeleton fallbacks (sidebar/header already mounted) */}
              <Route path="/home" element={renderLazyRoute(<DashboardPage />, <DashboardSkeleton />)} />
              <Route path="/profile" element={renderLazyRoute(<ProfilePage />, <ProfileSkeleton />)} />
              <Route path="/sessions" element={renderLazyRoute(<SessionsPage />, <SessionsSkeleton />)} />
              <Route path="/resources" element={renderLazyRoute(<LearningPage />, <LearningSkeleton />)} />
              <Route path="/tasks" element={renderLazyRoute(<TasksPage />, <TasksSkeleton />)} />
              {featureFlags.goals && GoalsPage && (
                <Route path="/goals" element={renderLazyRoute(<GoalsPage />, <GoalsSkeleton />)} />
              )}
              <Route path="/faq" element={renderLazyRoute(<FAQPage />, <FAQSkeleton />)} />
            </Route>
          </Route>

          {/* Fallback for unknown routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <AppToaster />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
